import { z } from 'zod';

// Dropdown choice configuration
export type DropdownChoice = {
  id: string;
  label: string;
  value: string;
  score?: number;
};

// Define template item type with recursive children support
export type TemplateItem = {
  id: string;
  label: string;
  type: 'ynna_actions_review' | 'dropdown' | 'text' | 'textarea' | 'number' | 'section';
  required?: boolean;  // Whether this item requires an answer
  config?: {
    choices?: DropdownChoice[];  // For dropdown type items
  };
  actions_enabled?: boolean;
  score?: {
    enabled?: boolean;
    weight?: number;
    map?: {
      Yes?: number | 'ignore';
      No?: number | 'ignore';
      'N/A'?: number | 'ignore';
      [key: string]: number | 'ignore' | undefined;  // Allow dynamic keys for dropdown scores
    };
  };
  children?: TemplateItem[];
  // Deprecated fields for backward compatibility
  itemType?: 'yes_no' | 'dropdown';
};

// Zod schema for dropdown choice
const zDropdownChoice = z.object({
  id: z.string(),
  label: z.string().min(1, "Choice label is required"),
  value: z.string().min(1, "Choice value is required"),
  score: z.number().optional()
});

// Recursive Zod schema for validation
const zTemplateItem: z.ZodType<TemplateItem> = z.lazy(() => z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(['ynna_actions_review', 'dropdown', 'text', 'textarea', 'number', 'section']),
  required: z.boolean().optional().default(false),
  config: z.object({
    choices: z.array(zDropdownChoice).min(2, "Dropdown must have at least 2 choices").optional()
  }).optional(),
  actions_enabled: z.boolean().optional().default(false),
  score: z.object({
    enabled: z.boolean().default(false),
    weight: z.number().default(1),
    map: z.record(z.union([z.number(), z.literal('ignore')])).default({
      Yes: 1,
      No: 0,
      'N/A': 'ignore'
    })
  }).optional(),
  children: z.array(zTemplateItem).optional().default([]),
  // Deprecated field for backward compatibility
  itemType: z.enum(['yes_no', 'dropdown']).optional()
}));

// Template content validation schema as specified
export const zTemplateContent = z.object({
  version: z.number().default(1),
  scoring_enabled: z.boolean().default(true),
  metaFields: z.array(z.object({
    key: z.string(),
    label: z.string(),
    type: z.enum(['auto', 'text', 'textarea', 'number']),
    readOnly: z.boolean().optional()
  })).default([
    { key: 'care_home', label: 'Care Home', type: 'auto', readOnly: true },
    { key: 'auditor_name', label: 'Auditor Name', type: 'auto', readOnly: true },
    { key: 'date_of_audit', label: 'Date of Audit', type: 'auto', readOnly: true },
    { key: 'job_role', label: 'Job Role', type: 'auto', readOnly: true }
  ]),
  tableColumns: z.array(z.string()).default([
    'Yes', 'No', 'N/A', 'Actions', 'Actions Reviewed (Date & Sign)'
  ]),
  items: z.array(zTemplateItem).default([]),
  signoff: z.array(z.object({
    key: z.string(),
    label: z.string(),
    type: z.enum(['auto', 'text'])
  })).default([
    { key: 'auditor_name', label: 'Auditor Name', type: 'auto' },
    { key: 'auditor_signature', label: 'Signature (typed)', type: 'text' },
    { key: 'auditor_signed_date', label: 'Date', type: 'auto' }
  ])
});

// Export the type for TypeScript usage
export type TemplateContent = z.infer<typeof zTemplateContent>;

// Helper function to create a new template item ID
export const generateItemId = (): string => {
  return `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to create a new dropdown choice ID
export const generateChoiceId = (): string => {
  return `choice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to create a new template content structure
export const createEmptyTemplateContent = (): TemplateContent => {
  return zTemplateContent.parse({});
};

// Validation helper for external use
export const validateTemplateContent = (content: unknown) => {
  return zTemplateContent.safeParse(content);
};

// Migration helper to convert old dropdown format to new format
export const migrateTemplateItems = (item: TemplateItem): TemplateItem => {
  // Convert old dropdown format (ynna_actions_review + itemType) to new dropdown type
  if (item.type === 'ynna_actions_review' && item.itemType === 'dropdown') {
    const migratedItem: TemplateItem = {
      ...item,
      type: 'dropdown',
      itemType: undefined, // Remove deprecated field
      score: undefined, // Remove scoring from dropdown items
      children: item.children ? item.children.map(migrateTemplateItems) : []
    };
    return migratedItem;
  }
  
  // Recursively migrate children
  const migratedItem = {
    ...item,
    children: item.children ? item.children.map(migrateTemplateItems) : []
  };
  return migratedItem;
};

// Migrate template content to add itemType to all items
export const migrateTemplateContent = (content: TemplateContent): TemplateContent => {
  return {
    ...content,
    items: content.items.map(migrateTemplateItems)
  };
};